import * as THREE from 'three';

const HEIGHT_RES = 96;
const VOLUME_RES = 32;
const HEIGHT_EMPTY = -1e24;

const MODE_OFF = 0;
const MODE_INSIDE = 1;
const MODE_OUTSIDE = 2;
const MODE_ABOVE = 3;
const MODE_BELOW = 4;

const MODE_ID = {
  inside: MODE_INSIDE,
  outside: MODE_OUTSIDE,
  above: MODE_ABOVE,
  below: MODE_BELOW,
};

let dummyHeight = null;
let dummyVolume = null;
let activeBake = null;
let markedSource = null;
let bakeCacheKey = '';

function getDummyHeight() {
  if (!dummyHeight) {
    dummyHeight = new THREE.DataTexture(new Float32Array([HEIGHT_EMPTY]), 1, 1, THREE.RedFormat, THREE.FloatType);
    dummyHeight.unpackAlignment = 1;
    dummyHeight.needsUpdate = true;
  }
  return dummyHeight;
}

function getDummyVolume() {
  if (!dummyVolume) {
    dummyVolume = new THREE.Data3DTexture(new Uint8Array([0]), 1, 1, 1);
    dummyVolume.format = THREE.RedFormat;
    dummyVolume.type = THREE.UnsignedByteType;
    dummyVolume.minFilter = THREE.NearestFilter;
    dummyVolume.magFilter = THREE.NearestFilter;
    dummyVolume.unpackAlignment = 1;
    dummyVolume.needsUpdate = true;
  }
  return dummyVolume;
}

export function findClipMesh(object) {
  if (!object) return null;
  if (object.isMesh && object.geometry?.attributes?.position) return object;
  let found = null;
  object.traverse?.((node) => {
    if (found || !node.isMesh || !node.geometry?.attributes?.position) return;
    found = node;
  });
  return found;
}

export function clipObjectLabel(object) {
  if (!object) return 'Object';
  return (
    object.userData?.mdvLayerKey ||
    object.name ||
    object.parent?.userData?.mdvLayerKey ||
    object.parent?.name ||
    'Object'
  );
}

function edgeKey(a, b) {
  return a < b ? `${a}_${b}` : `${b}_${a}`;
}

export function isGeometryClosed(geometry) {
  if (!geometry?.index || !geometry.attributes?.position) return false;
  const idx = geometry.index.array;
  if (idx.length < 12) return false;
  const counts = new Map();
  for (let i = 0; i < idx.length; i += 3) {
    const a = idx[i];
    const b = idx[i + 1];
    const c = idx[i + 2];
    if (a === b || b === c || c === a) return false;
    const keys = [edgeKey(a, b), edgeKey(b, c), edgeKey(c, a)];
    for (let k = 0; k < 3; k += 1) {
      counts.set(keys[k], (counts.get(keys[k]) || 0) + 1);
    }
  }
  let edges = 0;
  for (const n of counts.values()) {
    if (n !== 2) return false;
    edges += 1;
  }
  return edges >= 6;
}

function worldBoxOf(mesh) {
  mesh.updateWorldMatrix(true, true);
  const box = new THREE.Box3().setFromObject(mesh);
  if (box.isEmpty()) {
    box.set(new THREE.Vector3(-1, -1, -1), new THREE.Vector3(1, 1, 1));
  }
  box.expandByScalar(0.05);
  return box;
}

function bakeHeightMap(mesh, box) {
  const data = new Float32Array(HEIGHT_RES * HEIGHT_RES);
  data.fill(HEIGHT_EMPTY);
  const pos = mesh.geometry.attributes.position;
  const matrix = mesh.matrixWorld;
  const v = new THREE.Vector3();
  const size = new THREE.Vector3();
  box.getSize(size);
  const dx = Math.max(size.x, 1e-4);
  const dz = Math.max(size.z, 1e-4);
  const splat = (x, y, z) => {
    const u = Math.floor(((x - box.min.x) / dx) * (HEIGHT_RES - 1));
    const vIdx = Math.floor(((z - box.min.z) / dz) * (HEIGHT_RES - 1));
    if (u < 0 || vIdx < 0 || u >= HEIGHT_RES || vIdx >= HEIGHT_RES) return;
    const i = vIdx * HEIGHT_RES + u;
    if (y > data[i]) data[i] = y;
  };
  for (let i = 0; i < pos.count; i += 1) {
    v.fromBufferAttribute(pos, i).applyMatrix4(matrix);
    splat(v.x, v.y, v.z);
  }
  const idx = mesh.geometry.index;
  if (idx && pos.count < 250000) {
    const arr = idx.array;
    const a = new THREE.Vector3();
    const b = new THREE.Vector3();
    const c = new THREE.Vector3();
    const step = arr.length > 300000 ? 9 : 3;
    for (let i = 0; i < arr.length; i += step) {
      a.fromBufferAttribute(pos, arr[i]).applyMatrix4(matrix);
      b.fromBufferAttribute(pos, arr[i + 1]).applyMatrix4(matrix);
      c.fromBufferAttribute(pos, arr[i + 2]).applyMatrix4(matrix);
      splat((a.x + b.x) * 0.5, (a.y + b.y) * 0.5, (a.z + b.z) * 0.5);
      splat((b.x + c.x) * 0.5, (b.y + c.y) * 0.5, (b.z + c.z) * 0.5);
      splat((c.x + a.x) * 0.5, (c.y + a.y) * 0.5, (c.z + a.z) * 0.5);
    }
  }
  // Fill holes from nearest written cell
  const filled = data.slice();
  for (let y = 0; y < HEIGHT_RES; y += 1) {
    for (let x = 0; x < HEIGHT_RES; x += 1) {
      const i = y * HEIGHT_RES + x;
      if (filled[i] > HEIGHT_EMPTY * 0.5) continue;
      let best = HEIGHT_EMPTY;
      let bestD = 1e9;
      const r = 4;
      for (let dy = -r; dy <= r; dy += 1) {
        for (let dx = -r; dx <= r; dx += 1) {
          const xx = x + dx;
          const yy = y + dy;
          if (xx < 0 || yy < 0 || xx >= HEIGHT_RES || yy >= HEIGHT_RES) continue;
          const h = data[yy * HEIGHT_RES + xx];
          if (h < HEIGHT_EMPTY * 0.5) continue;
          const d = dx * dx + dy * dy;
          if (d < bestD) {
            bestD = d;
            best = h;
          }
        }
      }
      filled[i] = best;
    }
  }
  const tex = new THREE.DataTexture(filled, HEIGHT_RES, HEIGHT_RES, THREE.RedFormat, THREE.FloatType);
  tex.wrapS = THREE.ClampToEdgeWrapping;
  tex.wrapT = THREE.ClampToEdgeWrapping;
  tex.minFilter = THREE.LinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.generateMipmaps = false;
  tex.unpackAlignment = 1;
  tex.needsUpdate = true;
  return tex;
}

function bakeVolume(mesh, box) {
  const n = VOLUME_RES;
  const grid = new Uint8Array(n * n * n);
  const pos = mesh.geometry.attributes.position;
  const idx = mesh.geometry.index;
  if (!idx || pos.count > 400000 || idx.count > 900000) return { tex: getDummyVolume(), closed: false };
  const matrix = mesh.matrixWorld;
  const size = new THREE.Vector3();
  box.getSize(size);
  const inv = new THREE.Vector3(
    (n - 1) / Math.max(size.x, 1e-4),
    (n - 1) / Math.max(size.y, 1e-4),
    (n - 1) / Math.max(size.z, 1e-4)
  );
  const va = new THREE.Vector3();
  const vb = new THREE.Vector3();
  const vc = new THREE.Vector3();
  const arr = idx.array;
  const mark = (x, y, z) => {
    const xi = Math.max(0, Math.min(n - 1, x));
    const yi = Math.max(0, Math.min(n - 1, y));
    const zi = Math.max(0, Math.min(n - 1, z));
    grid[xi + yi * n + zi * n * n] = 1;
  };
  for (let i = 0; i < arr.length; i += 3) {
    va.fromBufferAttribute(pos, arr[i]).applyMatrix4(matrix);
    vb.fromBufferAttribute(pos, arr[i + 1]).applyMatrix4(matrix);
    vc.fromBufferAttribute(pos, arr[i + 2]).applyMatrix4(matrix);
    const minX = Math.floor((Math.min(va.x, vb.x, vc.x) - box.min.x) * inv.x);
    const maxX = Math.ceil((Math.max(va.x, vb.x, vc.x) - box.min.x) * inv.x);
    const minY = Math.floor((Math.min(va.y, vb.y, vc.y) - box.min.y) * inv.y);
    const maxY = Math.ceil((Math.max(va.y, vb.y, vc.y) - box.min.y) * inv.y);
    const minZ = Math.floor((Math.min(va.z, vb.z, vc.z) - box.min.z) * inv.z);
    const maxZ = Math.ceil((Math.max(va.z, vb.z, vc.z) - box.min.z) * inv.z);
    for (let z = minZ; z <= maxZ; z += 1) {
      for (let y = minY; y <= maxY; y += 1) {
        for (let x = minX; x <= maxX; x += 1) mark(x, y, z);
      }
    }
  }
  // Flood-fill outside (2) from the border
  const stack = [];
  const push = (x, y, z) => {
    if (x < 0 || y < 0 || z < 0 || x >= n || y >= n || z >= n) return;
    const i = x + y * n + z * n * n;
    if (grid[i] !== 0) return;
    grid[i] = 2;
    stack.push(i);
  };
  for (let y = 0; y < n; y += 1) {
    for (let x = 0; x < n; x += 1) {
      push(x, y, 0);
      push(x, y, n - 1);
    }
  }
  for (let z = 0; z < n; z += 1) {
    for (let x = 0; x < n; x += 1) {
      push(x, 0, z);
      push(x, n - 1, z);
    }
    for (let y = 0; y < n; y += 1) {
      push(0, y, z);
      push(n - 1, y, z);
    }
  }
  while (stack.length) {
    const i = stack.pop();
    const x = i % n;
    const y = Math.floor(i / n) % n;
    const z = Math.floor(i / (n * n));
    push(x - 1, y, z);
    push(x + 1, y, z);
    push(x, y - 1, z);
    push(x, y + 1, z);
    push(x, y, z - 1);
    push(x, y, z + 1);
  }
  let interior = 0;
  const occ = new Uint8Array(n * n * n);
  for (let i = 0; i < grid.length; i += 1) {
    if (grid[i] !== 2) {
      occ[i] = 255;
      if (grid[i] === 0) interior += 1;
    }
  }
  if (interior < 8) {
    return { tex: getDummyVolume(), closed: false };
  }
  const tex = new THREE.Data3DTexture(occ, n, n, n);
  tex.format = THREE.RedFormat;
  tex.type = THREE.UnsignedByteType;
  tex.minFilter = THREE.NearestFilter;
  tex.magFilter = THREE.NearestFilter;
  tex.unpackAlignment = 1;
  tex.needsUpdate = true;
  return { tex, closed: true };
}

function disposeBake(bake) {
  if (!bake) return;
  if (bake.height && bake.height !== getDummyHeight()) bake.height.dispose();
  if (bake.volume && bake.volume !== getDummyVolume()) bake.volume.dispose();
}

export function bakeClipObject(mesh) {
  if (!mesh?.geometry) return null;
  const box = worldBoxOf(mesh);
  const closedGeom = isGeometryClosed(mesh.geometry);
  const height = bakeHeightMap(mesh, box);
  let volume = getDummyVolume();
  let closed = false;
  let usedBox = true;
  if (closedGeom) {
    const vol = bakeVolume(mesh, box);
    volume = vol.tex;
    closed = vol.closed;
    usedBox = !vol.closed;
  }
  return {
    uuid: mesh.uuid,
    name: clipObjectLabel(mesh),
    box,
    closed,
    usedBox,
    height,
    volume,
    min: box.min.clone(),
    max: box.max.clone(),
  };
}

export function setActiveObjectClipBake(bake) {
  if (activeBake === bake) return;
  if (activeBake) disposeBake(activeBake);
  activeBake = bake || null;
  if (!bake) bakeCacheKey = '';
}

/** Rebuild occupancy/height textures only when the mesh pose changes. */
export function bakeClipObjectIfNeeded(mesh) {
  if (!mesh?.geometry) {
    setActiveObjectClipBake(null);
    return null;
  }
  mesh.updateWorldMatrix(true, false);
  const e = mesh.matrixWorld.elements;
  let hash = mesh.uuid;
  for (let i = 0; i < 16; i += 1) hash += `,${Math.round(e[i] * 1000)}`;
  if (activeBake && bakeCacheKey === hash) return activeBake;
  const bake = bakeClipObject(mesh);
  bakeCacheKey = hash;
  setActiveObjectClipBake(bake);
  return bake;
}

export function getActiveObjectClipBake() {
  return activeBake;
}

export function markClipSource(scene, uuid) {
  if (markedSource && markedSource !== uuid) {
    scene?.traverse((node) => {
      if (node.userData?.mdvClipSource) node.userData.mdvClipSource = false;
    });
  }
  markedSource = uuid || null;
  if (!uuid || !scene) return;
  scene.traverse((node) => {
    if (node.uuid === uuid) {
      node.userData.mdvClipSource = true;
      node.userData.mdvSkipClip = true;
    }
  });
}

function sampleHeight(bake, x, z) {
  const { min, max, height } = bake;
  const data = height.image.data;
  const w = height.image.width;
  const h = height.image.height;
  const u = (x - min.x) / Math.max(max.x - min.x, 1e-4);
  const v = (z - min.z) / Math.max(max.z - min.z, 1e-4);
  const ix = Math.max(0, Math.min(w - 1, Math.round(u * (w - 1))));
  const iy = Math.max(0, Math.min(h - 1, Math.round(v * (h - 1))));
  return data[iy * w + ix];
}

function sampleVolume(bake, point) {
  const { min, max, volume } = bake;
  const n = volume.image.width;
  const u = (point.x - min.x) / Math.max(max.x - min.x, 1e-4);
  const v = (point.y - min.y) / Math.max(max.y - min.y, 1e-4);
  const w = (point.z - min.z) / Math.max(max.z - min.z, 1e-4);
  if (u < 0 || v < 0 || w < 0 || u > 1 || v > 1 || w > 1) return false;
  const x = Math.max(0, Math.min(n - 1, Math.round(u * (n - 1))));
  const y = Math.max(0, Math.min(n - 1, Math.round(v * (n - 1))));
  const z = Math.max(0, Math.min(n - 1, Math.round(w * (n - 1))));
  return volume.image.data[x + y * n + z * n * n] > 127;
}

function inBox(bake, point) {
  const { min, max } = bake;
  return (
    point.x >= min.x &&
    point.x <= max.x &&
    point.y >= min.y &&
    point.y <= max.y &&
    point.z >= min.z &&
    point.z <= max.z
  );
}

/** True when a world point should be hidden by the object clip. */
export function isPointObjectClipped(point, mode) {
  if (!activeBake || !mode || mode === 'off') return false;
  if (mode === 'inside') {
    const inside = activeBake.closed ? sampleVolume(activeBake, point) : inBox(activeBake, point);
    return !inside;
  }
  if (mode === 'outside') {
    const inside = activeBake.closed ? sampleVolume(activeBake, point) : inBox(activeBake, point);
    return inside;
  }
  const h = sampleHeight(activeBake, point.x, point.z);
  if (h < HEIGHT_EMPTY * 0.5) return false;
  if (mode === 'above') return point.y < h;
  if (mode === 'below') return point.y > h;
  return false;
}

const CLIP_GLSL = `
varying vec3 vMdvWorldPos;
uniform int uMdvObjClip;
uniform vec3 uMdvObjMin;
uniform vec3 uMdvObjMax;
uniform float uMdvObjClosed;
uniform sampler2D uMdvObjHeight;
uniform sampler3D uMdvObjVolume;
#if __VERSION__ >= 300
  #define mdvTex2D(t, uv) texture(t, uv)
  #define mdvTex3D(t, uv) texture(t, uv)
#else
  #define mdvTex2D(t, uv) texture2D(t, uv)
  #define mdvTex3D(t, uv) texture3D(t, uv)
#endif
void mdvApplyObjectClip(vec3 p) {
  if (uMdvObjClip < 1) return;
  if (uMdvObjClip <= 2) {
    bool insideVol;
    if (uMdvObjClosed > 0.5) {
      vec3 d = max(uMdvObjMax - uMdvObjMin, vec3(1e-4));
      vec3 u = (p - uMdvObjMin) / d;
      if (any(lessThan(u, vec3(0.0))) || any(greaterThan(u, vec3(1.0)))) insideVol = false;
      else insideVol = mdvTex3D(uMdvObjVolume, u).r > 0.5;
    } else {
      insideVol = all(greaterThanEqual(p, uMdvObjMin)) && all(lessThanEqual(p, uMdvObjMax));
    }
    if (uMdvObjClip == 1 && !insideVol) discard;
    if (uMdvObjClip == 2 && insideVol) discard;
    return;
  }
  vec3 d2 = max(uMdvObjMax - uMdvObjMin, vec3(1e-4));
  vec2 huv = clamp(vec2((p.x - uMdvObjMin.x) / d2.x, (p.z - uMdvObjMin.z) / d2.z), 0.0, 1.0);
  float h = mdvTex2D(uMdvObjHeight, huv).r;
  if (h < -1.0e20) return;
  if (uMdvObjClip == 3 && p.y < h) discard;
  if (uMdvObjClip == 4 && p.y > h) discard;
}
`;

export function objectClipUniforms() {
  return {
    uMdvObjClip: { value: 0 },
    uMdvObjMin: { value: new THREE.Vector3(-1, -1, -1) },
    uMdvObjMax: { value: new THREE.Vector3(1, 1, 1) },
    uMdvObjClosed: { value: 0 },
    uMdvObjHeight: { value: getDummyHeight() },
    uMdvObjVolume: { value: getDummyVolume() },
  };
}

let lastClipMode = 'inside';
let lastClipOn = false;

export function writeObjectClipUniforms(uniforms, mode, enabled) {
  lastClipMode = mode;
  lastClipOn = Boolean(enabled);
  if (!uniforms?.uMdvObjClip) return;
  const on = Boolean(enabled && activeBake && mode && MODE_ID[mode]);
  uniforms.uMdvObjClip.value = on ? MODE_ID[mode] : 0;
  if (!on || !activeBake) return;
  uniforms.uMdvObjMin.value.copy(activeBake.min);
  uniforms.uMdvObjMax.value.copy(activeBake.max);
  uniforms.uMdvObjClosed.value = activeBake.closed ? 1 : 0;
  uniforms.uMdvObjHeight.value = activeBake.height || getDummyHeight();
  uniforms.uMdvObjVolume.value = activeBake.volume || getDummyVolume();
}

export const OBJECT_CLIP_SHADER_HEADER = CLIP_GLSL;

function injectShaderMaterialClip(material) {
  if (!material.uniforms) material.uniforms = {};
  if (!material.uniforms.uMdvObjClip) {
    Object.assign(material.uniforms, objectClipUniforms());
  }
  if (!material.vertexShader?.includes('vMdvWorldPos')) {
    material.vertexShader = `varying vec3 vMdvWorldPos;\n${material.vertexShader}`;
    if (material.vertexShader.includes('#include <clipping_planes_vertex>')) {
      material.vertexShader = material.vertexShader.replace(
        '#include <clipping_planes_vertex>',
        `#include <clipping_planes_vertex>
        vMdvWorldPos = (modelMatrix * vec4(position, 1.0)).xyz;`
      );
    } else {
      material.vertexShader = material.vertexShader.replace(
        /void\s+main\s*\(\s*\)\s*\{/,
        `void main() {
        vMdvWorldPos = (modelMatrix * vec4(position, 1.0)).xyz;`
      );
    }
  }
  if (!material.fragmentShader?.includes('mdvApplyObjectClip')) {
    material.fragmentShader = `${CLIP_GLSL}\n${material.fragmentShader}`;
    if (material.fragmentShader.includes('#include <clipping_planes_fragment>')) {
      material.fragmentShader = material.fragmentShader.replace(
        '#include <clipping_planes_fragment>',
        `#include <clipping_planes_fragment>
        mdvApplyObjectClip(vMdvWorldPos);`
      );
    } else {
      material.fragmentShader = material.fragmentShader.replace(
        /void\s+main\s*\(\s*\)\s*\{/,
        `void main() {
        mdvApplyObjectClip(vMdvWorldPos);`
      );
    }
  }
}

export function patchMaterialForObjectClip(material) {
  if (!material || material.isDerivedMaterial || material.isTroikaTextMaterial) return null;
  if (material.userData?.mdvObjClipPatched) {
    if (material.userData?.mdvObjUniforms) return material.userData.mdvObjUniforms;
    if (material.isShaderMaterial) return material.uniforms;
    return null;
  }
  if (material.isShaderMaterial) {
    injectShaderMaterialClip(material);
    material.userData.mdvObjClipPatched = true;
    material.userData.mdvObjUniforms = material.uniforms;
    material.needsUpdate = true;
    return material.uniforms;
  }
  const prev = material.onBeforeCompile;
  material.onBeforeCompile = (shader, renderer) => {
    prev?.(shader, renderer);
    if (!shader.uniforms.uMdvObjClip) {
      Object.assign(shader.uniforms, objectClipUniforms());
    }
    if (!shader.vertexShader.includes('vMdvWorldPos')) {
      shader.vertexShader = `varying vec3 vMdvWorldPos;\n${shader.vertexShader}`.replace(
        '#include <project_vertex>',
        `#include <project_vertex>
        {
          vec4 mdvWp = vec4(transformed, 1.0);
          #ifdef USE_INSTANCING
            mdvWp = instanceMatrix * mdvWp;
          #endif
          vMdvWorldPos = (modelMatrix * mdvWp).xyz;
        }`
      );
    }
    if (!shader.fragmentShader.includes('mdvApplyObjectClip')) {
      shader.fragmentShader = `${CLIP_GLSL}\n${shader.fragmentShader}`.replace(
        '#include <clipping_planes_fragment>',
        `#include <clipping_planes_fragment>
        mdvApplyObjectClip(vMdvWorldPos);`
      );
    }
    material.userData.mdvObjUniforms = shader.uniforms;
    writeObjectClipUniforms(shader.uniforms, lastClipMode, lastClipOn);
  };
  material.customProgramCacheKey = function mdvObjClipKey() {
    const base = prev?.customProgramCacheKey?.call(this) || '';
    return `${base}|mdv-obj-clip`;
  };
  material.userData.mdvObjClipPatched = true;
  material.needsUpdate = true;
  return material.userData.mdvObjUniforms || null;
}

export const patchMaterialObjectClip = patchMaterialForObjectClip;

export function objectClipStatus(mode, bake) {
  if (!bake || !mode) return '';
  const how = bake.closed ? 'closed mesh' : 'bounding box';
  if (mode === 'inside') return `Inside ${bake.name} (${how})`;
  if (mode === 'outside') return `Outside ${bake.name} (${how})`;
  if (mode === 'above') return `Above ${bake.name} (surface)`;
  if (mode === 'below') return `Below ${bake.name} (surface)`;
  return bake.name;
}
